import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getCourses, getProgress } from "@/services/api";
import Layout from "@/components/Layout";
import {
  BookOpen, CheckCircle2, Clock, Award, ChevronRight, AlertCircle
} from "lucide-react";

function ProgressBar({ value, max }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div
      className="w-full h-3 relative overflow-hidden"
      style={{
        background: "#fff",
        borderRadius: "99px",
        border: "1.5px solid #242220",
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          background: "#A1E1B9",
          height: "100%",
          borderRadius: "99px",
          borderRight: pct > 0 && pct < 100 ? "1.5px solid #242220" : "none",
          transition: "width 0.6s ease",
        }}
      />
    </div>
  );
}

function StatusBadge({ status, certNumber }) {
  if (status === "completed" || certNumber) {
    return (
      <span
        className="flex items-center gap-1 text-xs font-bold px-3 py-1"
        style={{
          background: "#A1E1B9",
          border: "1.5px solid #242220",
          borderRadius: "99px",
          color: "#242220",
        }}
      >
        <CheckCircle2 size={12} strokeWidth={2.5} /> Befejezve
      </span>
    );
  }
  if (status === "assigned") {
    return (
      <span
        className="flex items-center gap-1 text-xs font-bold px-3 py-1"
        style={{
          background: "#FBD556",
          border: "1.5px solid #242220",
          borderRadius: "99px",
          color: "#242220",
        }}
      >
        <Clock size={12} strokeWidth={2.5} /> Folyamatban
      </span>
    );
  }
  return null;
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "Jó reggelt";
  if (hour >= 12 && hour < 18) return "Jó napot";
  if (hour >= 18 && hour < 22) return "Jó estét";
  return "Jó éjszakát";
}

export default function DashboardPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user?.id) return;
    const load = async () => {
      setLoading(true);
      try {
        const [cRes, pRes] = await Promise.all([
          getCourses(token, user.id),
          getProgress(token, user.id),
        ]);
        setCourses(cRes.data);
        // index by course_identifier
        const pm = {};
        (pRes.data || []).forEach((p) => { pm[p.course_identifier] = p; });
        setProgress(pm);
      } catch (err) {
        setError("Nem sikerült betölteni a kurzusokat. Kérjük próbálja újra.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, token]);

  const completedCount = courses.filter(
    (c) => c.status === "completed" || c.certificate_number
  ).length;

  const inProgressCount = courses.filter((c) => {
    // Not completed
    if (c.status === "completed" || c.certificate_number) return false;
    // Has some progress (started lessons or attempted exam)
    const prog = progress[c.course_identifier];
    return prog && (prog.lessons_completed?.length > 0 || prog.exam_attempts > 0);
  }).length;

  return (
    <Layout>
      <div data-testid="dashboard-page">
        {/* Header */}
        <div className="mb-8">
          <h1
            className="text-4xl font-bold mb-1"
            style={{ color: "#242220", fontFamily: "Quicksand, sans-serif" }}
          >
            {getGreeting()}, {user?.name?.split(" ").pop() || "Tanuló"}!
          </h1>
          <p className="text-base" style={{ color: "#242220", opacity: 0.6 }}>
            Folytatd a tanulást ott, ahol abbahagytad.
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {[
            {
              label: "Hozzárendelt kurzus",
              value: courses.length,
              color: "#FBD556",
              icon: BookOpen,
              testId: "stat-total",
            },
            {
              label: "Folyamatban",
              value: inProgressCount,
              color: "#FF9456",
              icon: Clock,
              testId: "stat-inprogress",
            },
            {
              label: "Befejezett",
              value: completedCount,
              color: "#A1E1B9",
              icon: Award,
              testId: "stat-completed",
            },
          ].map((s) => (
            <div
              key={s.label}
              className="p-5"
              style={{
                background: s.color,
                borderRadius: "24px",
                border: "1.5px solid #242220",
                boxShadow: "4px 4px 0 #242220",
              }}
              data-testid={s.testId}
            >
              <s.icon size={22} strokeWidth={2.5} color="#242220" />
              <p
                className="text-3xl font-bold mt-2"
                style={{ color: "#242220" }}
              >
                {s.value}
              </p>
              <p className="text-xs font-bold" style={{ color: "#242220", opacity: 0.7 }}>
                {s.label}
              </p>
            </div>
          ))}
        </div>

        {/* Courses heading */}
        <h2 className="text-xl font-bold mb-4" style={{ color: "#242220" }}>
          Saját Kurzusok
        </h2>

        {loading && (
          <div className="text-center py-16" style={{ color: "#242220", opacity: 0.5 }}>
            <div className="text-4xl mb-3">...</div>
            <p className="font-bold">Kurzusok betöltése...</p>
          </div>
        )}

        {error && (
          <div
            className="flex items-center gap-3 p-4 mb-6"
            style={{
              background: "#FDECEA",
              border: "1.5px solid #D32F2F",
              borderRadius: "16px",
              color: "#D32F2F",
            }}
            data-testid="courses-error"
          >
            <AlertCircle size={20} strokeWidth={2.5} />
            <p className="font-bold text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && courses.length === 0 && (
          <div
            className="text-center py-16 p-8"
            style={{
              background: "#fff",
              borderRadius: "24px",
              border: "1.5px solid #242220",
              boxShadow: "4px 4px 0 #242220",
            }}
            data-testid="no-courses"
          >
            <BookOpen size={48} strokeWidth={1.5} color="#242220" style={{ opacity: 0.3, margin: "0 auto 16px" }} />
            <p className="font-bold text-lg" style={{ color: "#242220" }}>
              Még nincs hozzárendelt kurzus
            </p>
            <p className="text-sm mt-2" style={{ color: "#242220", opacity: 0.5 }}>
              Kurzusok hamarosan megjelennek itt.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {courses.map((course, idx) => {
            const prog = progress[course.course_identifier];
            const lessonsCompleted = prog?.lessons_completed?.length || 0;
            const totalLessons = course.lesson_count || 0;
            const examPassed = prog?.exam_passed || false;
            const isCompleted = course.status === "completed" || !!course.certificate_number;

            return (
              <div
                key={course.assignment_id || idx}
                className="p-6 flex flex-col gap-4 cursor-pointer transition-all hover:translate-x-[2px] hover:translate-y-[2px]"
                style={{
                  background: "#fff",
                  borderRadius: "24px",
                  border: "1.5px solid #242220",
                  boxShadow: "6px 6px 0 #242220",
                }}
                onClick={() =>
                  course.has_local_content
                    ? navigate(`/courses/${encodeURIComponent(course.course_identifier)}`)
                    : null
                }
                data-testid={`course-card-${idx}`}
              >
                {course.cover_image && (
                  <img
                    src={course.cover_image}
                    alt={course.course_name}
                    className="w-full object-cover"
                    style={{ borderRadius: "16px", border: "1.5px solid #242220", height: 140 }}
                  />
                )}

                <div className="flex items-start justify-between gap-2">
                  <h3
                    className="font-bold text-base leading-snug"
                    style={{ color: "#242220" }}
                  >
                    {course.course_name}
                  </h3>
                  <StatusBadge status={course.status} certNumber={course.certificate_number} />
                </div>

                {course.description && (
                  <p className="text-xs" style={{ color: "#242220", opacity: 0.6, lineHeight: 1.5 }}>
                    {course.description.slice(0, 100)}
                    {course.description.length > 100 ? "…" : ""}
                  </p>
                )}

                {course.has_local_content && totalLessons > 0 && (
                  <div>
                    <div className="flex justify-between text-xs font-bold mb-2" style={{ color: "#242220" }}>
                      <span>Haladás</span>
                      <span>{lessonsCompleted}/{totalLessons} lecke</span>
                    </div>
                    <ProgressBar value={lessonsCompleted} max={totalLessons} />
                  </div>
                )}

                {!course.has_local_content && (
                  <p className="text-xs italic" style={{ color: "#242220", opacity: 0.4 }}>
                    Tartalom hamarosan elérhető
                  </p>
                )}

                {course.has_local_content && (
                  <button
                    className="flex items-center justify-center gap-2 py-2.5 font-bold text-sm mt-auto transition-all hover:translate-x-[1px] hover:translate-y-[1px] active:translate-x-[3px] active:translate-y-[3px]"
                    style={{
                      background: isCompleted ? "#A1E1B9" : "#FBD556",
                      borderRadius: "30px",
                      border: "1.5px solid #242220",
                      boxShadow: "3px 3px 0 #242220",
                      color: "#242220",
                      cursor: "pointer",
                    }}
                    data-testid={`course-start-btn-${idx}`}
                  >
                    {isCompleted ? (
                      <><Award size={15} strokeWidth={2.5} /> Bizonyítvány</>
                    ) : lessonsCompleted > 0 ? (
                      <>Folytatás <ChevronRight size={15} strokeWidth={2.5} /></>
                    ) : (
                      <>Kurzus kezdése <ChevronRight size={15} strokeWidth={2.5} /></>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
